import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Search, AlertTriangle, CheckCircle, FileSearch, Users, Clock, DollarSign, Target } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { TestimonialSlider } from '../../components/TestimonialSlider';
import { PricingSection } from '../../components/PricingSection';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { trademarkPricing } from '../../pricingData';
import { faqs } from '../../mock';
import { useScrollAnimation, useCounterAnimation } from '../../hooks/useScrollAnimation';

const StatCard = ({ icon: Icon, value, label, delay }) => {
  const [ref, count] = useCounterAnimation(parseInt(value), 2000);
  
  return (
    <div ref={ref} className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all" style={{ animationDelay: `${delay}ms` }}>
      <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
        <Icon className="text-orange-600" size={32} />
      </div>
      <div className="text-4xl font-bold text-gray-900 mb-2">{count}+</div>
      <div className="text-gray-600">{label}</div>
    </div>
  );
};

export const TrademarkSearch = ({ onOpenPopup }) => {
  const [statsRef, statsVisible] = useScrollAnimation(0.2);
  const [benefitsRef, benefitsVisible] = useScrollAnimation(0.2);

  const faqs = [
    {
      id: 1,
      question: "Why do I need a trademark search?",
      answer: "A trademark search identifies existing trademarks that could conflict with your application and cause rejection. It saves you time, money, and potential legal disputes by ensuring your mark is available before you invest in branding and filing fees."
    },
    {
      id: 2,
      question: "What's the difference between basic and comprehensive search?",
      answer: "Basic search covers the USPTO database for exact and near-matches. Comprehensive search includes federal, state, common law trademarks, domain names, business registrations, and social media handles - providing a complete picture of potential conflicts."
    },
    {
      id: 3,
      question: "How long does a trademark search take?",
      answer: "Basic searches can be completed within 24-48 hours. Comprehensive searches typically take 3-5 business days, depending on the complexity and number of variations analyzed."
    },
    {
      id: 4,
      question: "Can I file without a trademark search?",
      answer: "While not required, filing without a search is risky. You could waste application fees on a mark that will be refused, face rebranding costs, or encounter legal disputes. A search significantly reduces these risks."
    }
  ];

  return (
    <div className="pt-20">
      <Helmet>
        <title>Comprehensive Trademark Search Services | Avoid Legal Conflicts</title>
        <meta name="description" content="Professional trademark search services to identify potential conflicts before filing. Comprehensive database searches and expert analysis to protect your brand investment." />
        <meta name="keywords" content="trademark search, comprehensive search, USPTO search, trademark conflict check, brand availability search, trademark clearance" />
      </Helmet>

      {/* Creative Hero Section */}
      <section className="relative bg-gradient-to-br from-orange-600 via-orange-500 to-orange-700 text-white py-32 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-orange-400 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        </div>
        
        {/* Floating Elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-32 left-1/4 animate-float">
            <Search className="w-20 h-20 text-white/10" />
          </div>
          <div className="absolute bottom-32 right-1/3 animate-float" style={{ animationDelay: '1s' }}>
            <FileSearch className="w-24 h-24 text-white/10" />
          </div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-6 py-2 mb-6 animate-slide-in">
              <span className="text-sm font-semibold">🔍 Risk Mitigation</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-slide-in">Trademark Search</h1>
            <p className="text-xl mb-8 text-white/95 leading-relaxed max-w-3xl mx-auto animate-slide-in">
              Comprehensive trademark searches to identify potential conflicts before you file and protect your investment
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-in">
              <Button
                onClick={onOpenPopup}
                className="bg-white text-primary hover:bg-gray-100 h-12 px-8 text-base hover:scale-105 transition-all"
              >
                Start Your Search
              </Button>
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 h-12 px-8 text-base"
                onClick={() => document.getElementById('search-types').scrollIntoView({ behavior: 'smooth' })}
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V120Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Why Search is Important with Image */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-orange-50">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="order-2 lg:order-1">
                <div className="relative">
                  <div className="absolute -top-4 -left-4 w-72 h-72 bg-orange-200 rounded-full blur-3xl opacity-30"></div>
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                    <img 
                      src="https://images.unsplash.com/photo-1553877522-43269d4ea984?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw0fHxzZWFyY2glMjBkYXRhfGVufDB8fHx8MTc2MjU0ODk4OXww&ixlib=rb-4.1.0&q=85?w=800&h=600&fit=crop" 
                      alt="Data analysis" 
                      className="w-full h-96 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-orange-900/80 to-transparent flex items-end p-8">
                      <div className="text-white">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="bg-white/20 backdrop-blur-sm p-3 rounded-lg">
                            <Search size={32} />
                          </div>
                          <div>
                            <div className="text-2xl font-bold">Deep Analysis</div>
                            <div className="text-white/90">Comprehensive Research</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="inline-block bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  📚 Learn More
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Why Trademark Search Matters</h2>
                <div className="space-y-4 text-base text-gray-700 leading-relaxed">
                  <p>
                    Before investing time and money in a trademark application, it's crucial to determine whether your desired mark is available for registration. A comprehensive trademark search identifies existing trademarks that could conflict with your application and cause rejection.
                  </p>
                  <p>
                    The USPTO examines every application against their database of registered and pending trademarks. If your mark is too similar to an existing one in a related industry, your application will be refused. Additionally, using a mark that infringes on someone else's rights can result in costly legal disputes.
                  </p>
                  <div className="bg-white p-6 rounded-2xl shadow-lg border-l-4 border-orange-600">
                    <p className="font-semibold text-gray-900 mb-2">💡 Did You Know?</p>
                    <p className="text-gray-700">
                      Over 40% of trademark applications receive refusals, many due to conflicts that could have been identified with a proper search.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Search Types */}
      <section id="search-types" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Search Services</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Choose the level of protection that fits your needs
            </p>
          </div>
          <div 
            ref={benefitsRef}
            className={`grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto stagger-children ${benefitsVisible ? 'visible' : ''}`}
          >
            <div className="group relative bg-gradient-to-br from-orange-50 to-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all border border-orange-100">
              <div className="bg-orange-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Search className="text-orange-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Basic Search</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Quick search of the USPTO database for exact and near-matches to your proposed trademark. Ideal for initial screening and budget-conscious clients.
              </p>
            </div>

            <div className="group relative bg-gradient-to-br from-orange-50 to-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all border border-orange-100 ring-2 ring-orange-200">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-orange-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                RECOMMENDED
              </div>
              <div className="bg-orange-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <FileSearch className="text-orange-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Comprehensive Search</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                In-depth analysis including federal, state, and common law trademarks, plus domain names and business registrations for complete protection.
              </p>
            </div>

            <div className="group relative bg-gradient-to-br from-orange-50 to-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all border border-orange-100">
              <div className="bg-orange-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <CheckCircle className="text-orange-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Clearance Opinion</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Professional legal analysis of search results with expert recommendations on your trademark's registrability and risk assessment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Search */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">What We Search</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="flex gap-4">
              <div className="bg-primary/10 w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="text-primary" size={20} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Federal Trademarks</h3>
                <p className="text-gray-600 text-sm">All registered and pending trademarks in the USPTO database</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="bg-primary/10 w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="text-primary" size={20} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">State Registrations</h3>
                <p className="text-gray-600 text-sm">State-level trademark and business name registrations</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="bg-primary/10 w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="text-primary" size={20} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Common Law Uses</h3>
                <p className="text-gray-600 text-sm">Unregistered trademarks in use that could create conflicts</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="bg-primary/10 w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="text-primary" size={20} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Domain Names</h3>
                <p className="text-gray-600 text-sm">Active domain registrations that match or are similar to your mark</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="bg-primary/10 w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="text-primary" size={20} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Business Directories</h3>
                <p className="text-gray-600 text-sm">Business listings and trade directories for similar marks</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="bg-primary/10 w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="text-primary" size={20} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Social Media</h3>
                <p className="text-gray-600 text-sm">Social media handles and brand presence across platforms</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Risk Identification */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center">Identify Risks Early</h2>
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl p-8 shadow-lg border border-red-100">
              <div className="flex items-start gap-4 mb-6">
                <div className="bg-red-100 w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="text-red-600" size={32} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Avoid Costly Mistakes</h3>
                  <p className="text-base text-gray-600 leading-relaxed">
                    Investing in a trademark search saves you from wasting application fees, rebranding costs, and potential legal disputes. Our search reveals conflicts before you commit to your mark.
                  </p>
                </div>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-primary flex-shrink-0 mt-1" size={20} />
                  <span className="text-base">Reduce rejection risk by identifying conflicting marks</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-primary flex-shrink-0 mt-1" size={20} />
                  <span className="text-base">Save time and money by choosing a registrable mark upfront</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-primary flex-shrink-0 mt-1" size={20} />
                  <span className="text-base">Avoid infringement claims and costly rebranding</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-primary flex-shrink-0 mt-1" size={20} />
                  <span className="text-base">Make informed decisions with professional analysis</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      {/* Pricing Plans Section */}
      <div id="pricing">
        <PricingSection pricingData={trademarkPricing} onOpenPopup={onOpenPopup} />
      </div>


      {/* FAQs - Creative Design */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-block bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              ❓ Got Questions?
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to know about trademark searches
            </p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq) => (
                <AccordionItem key={faq.id} value={`item-${faq.id}`} className="bg-gradient-to-r from-orange-50 to-orange-50 rounded-2xl px-6 border-none shadow-sm hover:shadow-md transition-shadow">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-primary py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-base text-gray-600 pb-6 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialSlider />

      {/* CTA Section */}
      <section className="relative py-20 bg-gradient-to-br from-orange-600 via-orange-500 to-orange-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-96 h-96 bg-orange-400 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-400 rounded-full blur-3xl animate-pulse-slow"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Search Your Trademark Today</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-white/90">
            Protect your investment with a comprehensive trademark search before you file
          </p>
          <Button
            onClick={onOpenPopup}
            className="bg-white text-orange-600 hover:bg-gray-100 h-14 px-10 text-lg hover:scale-105 transition-all shadow-xl"
          >
            Start Your Search Now
          </Button>
        </div>
      </section>
    </div>
  );
};